import type { TurnEvent } from "../types";
import bundledPrices from "./prices.json";

export interface RawPrice {
  input: number | null;
  output: number | null;
  cache_read?: number | null;
  cache_write_5m?: number | null;
  cache_write_1h?: number | null;
  /** Fast-mode premium rates (cache multipliers stack on these). */
  fast_input?: number | null;
  fast_output?: number | null;
  /** Optional validity window (ISO dates) for time-dependent pricing. */
  from?: string;
  until?: string;
}

export type PriceEntry = RawPrice | RawPrice[];

export interface PricesFile {
  version: string;
  source?: string;
  currency?: string;
  per_mtok: Record<string, PriceEntry>;
}

export interface ResolvedPrice {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
}

export interface PriceContext {
  ts?: number;
  speed?: string;
  inferenceGeo?: string;
}

const US_GEO_MULTIPLIER = 1.1;

export class PriceTable {
  private readonly keysByLength: string[];

  constructor(private readonly file: PricesFile) {
    this.keysByLength = Object.keys(file.per_mtok).sort((a, b) => b.length - a.length);
  }

  static bundled(): PriceTable {
    return new PriceTable(bundledPrices as PricesFile);
  }

  /** Bundled table with a user's partial table merged on top (per-model override). */
  static withOverrideData(raw: Partial<PricesFile>): PriceTable {
    const base = bundledPrices as PricesFile;
    return new PriceTable({
      ...base,
      ...raw,
      per_mtok: { ...base.per_mtok, ...(raw.per_mtok ?? {}) },
    });
  }

  get version(): string {
    return this.file.version;
  }

  /**
   * Model id -> price entry via exact match, then longest-prefix match (covers
   * date-stamped ids like claude-haiku-4-5-20251001). Null when unknown.
   */
  resolve(model: string, ctx: PriceContext = {}): ResolvedPrice | null {
    const entry = this.file.per_mtok[model] ?? this.prefixMatch(model);
    if (!entry) return null;
    const variant = pickVariant(entry, ctx.ts);
    if (!variant || variant.input == null || variant.output == null) return null;

    let price: ResolvedPrice;
    if (ctx.speed === "fast" && variant.fast_input != null && variant.fast_output != null) {
      // Cache multipliers stack on top of fast-mode pricing.
      price = {
        input: variant.fast_input,
        output: variant.fast_output,
        cacheRead: variant.fast_input * 0.1,
        cacheWrite5m: variant.fast_input * 1.25,
        cacheWrite1h: variant.fast_input * 2,
      };
    } else {
      price = {
        input: variant.input,
        output: variant.output,
        cacheRead: variant.cache_read ?? variant.input * 0.1,
        cacheWrite5m: variant.cache_write_5m ?? variant.input * 1.25,
        cacheWrite1h: variant.cache_write_1h ?? variant.input * 2,
      };
    }
    if (ctx.inferenceGeo === "us") {
      price = {
        input: price.input * US_GEO_MULTIPLIER,
        output: price.output * US_GEO_MULTIPLIER,
        cacheRead: price.cacheRead * US_GEO_MULTIPLIER,
        cacheWrite5m: price.cacheWrite5m * US_GEO_MULTIPLIER,
        cacheWrite1h: price.cacheWrite1h * US_GEO_MULTIPLIER,
      };
    }
    return price;
  }

  /** Cost of one turn in USD. 0 for zero-usage turns; null when unpriceable. */
  turnCostUsd(turn: TurnEvent): number | null {
    return this.costAtModel(turn.model, turn, { keepSpeed: true });
  }

  /**
   * Cost of a turn's token counts billed as `model` — used for the "what if
   * this ran on the ideal model" comparison. Keeps the turn's inference geo
   * (switching model would not change it) but assumes standard speed unless
   * `keepSpeed` is set.
   */
  costAtModel(model: string, turn: TurnEvent, opts: { keepSpeed?: boolean } = {}): number | null {
    const u = turn.usage;
    if (u.input + u.output + u.cacheRead + u.cacheWrite === 0) return 0;
    const price = this.resolve(model, {
      ts: turn.timestamp > 0 ? turn.timestamp : undefined,
      speed: opts.keepSpeed ? turn.speed : undefined,
      inferenceGeo: turn.inferenceGeo,
    });
    if (!price) return null;
    // Prefer the 5m/1h breakdown when it accounts for the total; otherwise
    // bill all cache writes at the (cheaper) 5m rate — conservative.
    let write5m = u.cacheWrite5m;
    let write1h = u.cacheWrite1h;
    if (write5m + write1h !== u.cacheWrite) {
      write5m = u.cacheWrite;
      write1h = 0;
    }
    return (
      (u.input * price.input +
        u.output * price.output +
        u.cacheRead * price.cacheRead +
        write5m * price.cacheWrite5m +
        write1h * price.cacheWrite1h) /
      1e6
    );
  }

  private prefixMatch(model: string): PriceEntry | undefined {
    for (const key of this.keysByLength) {
      if (model.startsWith(key)) return this.file.per_mtok[key];
    }
    return undefined;
  }
}

function pickVariant(entry: PriceEntry, ts?: number): RawPrice | undefined {
  if (!Array.isArray(entry)) return entry;
  if (entry.length === 0) return undefined;
  const at = ts ?? Date.now();
  for (const v of entry) {
    const from = v.from ? Date.parse(v.from) : -Infinity;
    const until = v.until ? Date.parse(v.until) : Infinity;
    if (at >= from && at < until) return v;
  }
  return entry[entry.length - 1];
}
